import {
  Inbox,
  Send,
  FileText,
  Trash2,
  Plus,
  Sparkles,
  AlertCircle,
  Loader2,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useAccounts } from '../../hooks/useAccounts';
import { useFolders } from '../../hooks/useFolders';
import { useMailStore } from '../../store/mail';
import { useAuth } from '../../store/auth';

const SPECIAL_ICONS: Record<string, LucideIcon> = {
  '\\Inbox': Inbox,
  '\\Sent': Send,
  '\\Drafts': FileText,
  '\\Trash': Trash2,
  '\\Junk': AlertCircle,
};

export function Sidebar() {
  const { data: accounts, isLoading: accountsLoading } = useAccounts();
  const selectedAccountId = useMailStore((s) => s.selectedAccountId);
  const setSelectedAccount = useMailStore((s) => s.setSelectedAccount);
  const selectedFolderId = useMailStore((s) => s.selectedFolderId);
  const setSelectedFolder = useMailStore((s) => s.setSelectedFolder);
  const setAddAccountOpen = useMailStore((s) => s.setAddAccountOpen);

  const { data: folders } = useFolders(selectedAccountId ?? undefined);
  const { user, signOut } = useAuth();

  const hasAccounts = !!accounts && accounts.length > 0;

  return (
    <aside className="flex w-60 flex-col border-r border-zinc-800/80 bg-zinc-950/30">
      <div className="p-3">
        <button
          onClick={() => setAddAccountOpen(true)}
          className="flex w-full items-center justify-between rounded-md border border-accent-500/30 bg-accent-500/10 px-3 py-2 text-sm font-medium text-accent-300 transition hover:border-accent-500/50 hover:bg-accent-500/20"
        >
          <span className="flex items-center gap-2">
            <Plus size={14} />
            {hasAccounts ? 'Compose' : 'Aggiungi account'}
          </span>
          <kbd className="key-hint">{hasAccounts ? 'C' : 'A'}</kbd>
        </button>
      </div>

      {hasAccounts && (
        <div className="px-3 pb-2">
          <select
            value={selectedAccountId ?? ''}
            onChange={(e) => setSelectedAccount(e.target.value || null)}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1 text-xs text-zinc-300 outline-none focus:border-accent-500/50"
          >
            <option value="">Tutti gli account</option>
            {accounts!.map((a) => (
              <option key={a.id} value={a.id}>
                {a.email}
              </option>
            ))}
          </select>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2">
        {accountsLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={16} className="animate-spin text-zinc-600" />
          </div>
        )}

        {!hasAccounts && !accountsLoading && (
          <div className="px-2 py-6 text-center">
            <p className="text-xs text-zinc-500">
              Nessun account collegato. Aggiungi il tuo primo account per iniziare.
            </p>
          </div>
        )}

        {hasAccounts && folders && folders.length > 0 && (
          <ul className="space-y-0.5">
            {folders
              .slice()
              .sort((a, b) => specialOrder(a.special_use) - specialOrder(b.special_use))
              .map((f) => {
                const Icon: LucideIcon = (f.special_use ? SPECIAL_ICONS[f.special_use] : null) ?? Inbox;
                const isSelected = selectedFolderId === f.id;
                return (
                  <li key={f.id}>
                    <button
                      onClick={() => setSelectedFolder(f.id)}
                      className={
                        'group flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition ' +
                        (isSelected
                          ? 'bg-accent-500/15 text-accent-200'
                          : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')
                      }
                    >
                      <span className="flex items-center gap-2">
                        <Icon
                          size={14}
                          className={isSelected ? 'text-accent-400' : 'text-zinc-500'}
                        />
                        <span className="truncate">{f.name || f.path}</span>
                      </span>
                      {f.unread_count > 0 && (
                        <span className="rounded bg-zinc-800 px-1.5 text-[10px] text-zinc-300">
                          {f.unread_count}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
          </ul>
        )}

        <div className="mt-6">
          <div className="flex items-center justify-between px-2 pb-1">
            <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <Sparkles size={10} /> AI Tags
            </span>
            <button className="text-zinc-500 hover:text-zinc-300">
              <Plus size={12} />
            </button>
          </div>
          <div className="px-2 py-2 text-[11px] text-zinc-600">
            Le regole AI verranno attivate dopo aver collegato un account.
          </div>
        </div>
      </nav>

      <div className="border-t border-zinc-800/80 p-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 text-xs font-semibold uppercase">
            {user?.email?.[0] ?? '?'}
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="truncate text-xs font-medium">{user?.email}</div>
            <div className="truncate text-[10px] text-zinc-500">
              {accounts?.length ?? 0} account collegati
            </div>
          </div>
          <button
            onClick={() => signOut()}
            title="Esci"
            className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-300"
          >
            <LogOut size={12} />
          </button>
        </div>
      </div>
    </aside>
  );
}

function specialOrder(s: string | null): number {
  const map: Record<string, number> = {
    '\\Inbox': 1,
    '\\Drafts': 2,
    '\\Sent': 3,
    '\\Archive': 7,
    '\\Junk': 8,
    '\\Trash': 9,
  };
  return s ? (map[s] ?? 5) : 5;
}
